<template>
  <div class="detail-container">
    <div class="page-header">
      <h1 class="page-title">訂位詳情</h1>
      <div class="action-buttons">
        <button class="btn btn-secondary" @click="handleEdit">
          <i class="ri-edit-line"></i>
          編輯
        </button>
        <button class="btn btn-secondary" @click="handleBack">
          <i class="ri-arrow-left-line"></i>
          返回
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchReservation">重試</button>
    </div>

    <div v-else-if="reservation" class="detail-content">
      <div class="detail-section">
        <h3 class="section-title">訂位資訊</h3>
        <div class="detail-grid">
          <div class="detail-item">
            <label>訂位編號</label>
            <span>{{ reservation.reservationNumber || '-' }}</span>
          </div>
          <div class="detail-item">
            <label>訂位日期</label>
            <span>{{ formatDate(reservation.reservationDate) }}</span>
          </div>
          <div class="detail-item">
            <label>訂位時間</label>
            <span>{{ formatTimeToMinute(reservation.reservationTime) }}</span>
          </div>
          <div class="detail-item">
            <label>用餐人數</label>
            <span>{{ reservation.guestCount || 0 }}人</span>
          </div>
          <div class="detail-item">
            <label>狀態</label>
            <span :class="getStatusBadgeClass(reservation.status)">
              {{ getStatusText(reservation.status) }}
            </span>
          </div>
          <div class="detail-item table-assign-item">
            <label>座位</label>
            <div v-if="canChangeTable" class="table-assign-controls">
              <select
                v-model="selectedTableId"
                class="form-control table-select"
                :disabled="tablesLoading || savingTable"
              >
                <option value="" disabled>
                  {{ tablesLoading ? '載入可改派座位中...' : '請選擇座位' }}
                </option>
                <option
                  v-for="table in selectableTables"
                  :key="table.tableId"
                  :value="table.tableId"
                >
                  {{ formatSelectableTableLabel(table) }}
                </option>
              </select>
              <button
                type="button"
                class="btn btn-primary btn-sm"
                :disabled="!canSaveTable || savingTable"
                @click="handleSaveTable"
              >
                {{ savingTable ? '儲存中...' : '更新座位' }}
              </button>
              <span
                v-if="selectedTableOption?.isUpwardAssignment"
                class="badge badge-warning upward-badge"
              >向上相容</span>
              <p v-if="tableLoadError" class="table-hint table-hint-error">{{ tableLoadError }}</p>
              <p v-else-if="!tablesLoading && selectableTables.length === 0" class="table-hint">
                此時段沒有可改派的座位（可能已滿位）。
              </p>
            </div>
            <span v-else>
              {{ formatTableLabel(reservation) }}
              <span
                v-if="reservation.isUpwardTableAssignment"
                class="badge badge-warning upward-badge"
              >向上相容</span>
            </span>
          </div>
        </div>
      </div>

      <div class="detail-section">
        <h3 class="section-title">聯絡資訊</h3>
        <div class="detail-grid">
          <div class="detail-item">
            <label>聯絡人姓名</label>
            <span>{{ reservation.contactName || '-' }}</span>
          </div>
          <div class="detail-item">
            <label>聯絡電話</label>
            <span>{{ reservation.contactPhone || '-' }}</span>
          </div>
        </div>
      </div>

      <div v-if="reservation.specialRequests || reservation.occasion" class="detail-section">
        <h3 class="section-title">特殊需求</h3>
        <div class="detail-grid">
          <div v-if="reservation.occasion" class="detail-item">
            <label>用餐目的</label>
            <span>{{ reservation.occasion }}</span>
          </div>
          <div v-if="reservation.specialRequests" class="detail-item full-width">
            <label>特殊需求</label>
            <span>{{ reservation.specialRequests }}</span>
          </div>
        </div>
      </div>

      <div class="detail-section">
        <h3 class="section-title">時間記錄</h3>
        <div class="detail-grid">
          <div class="detail-item">
            <label>建立時間</label>
            <span>{{ formatDateTime(reservation.createdAt) }}</span>
          </div>
          <div v-if="reservation.arrivalTime" class="detail-item">
            <label>到店時間</label>
            <span>{{ formatDateTime(reservation.arrivalTime) }}</span>
          </div>
          <div v-if="reservation.seatedTime" class="detail-item">
            <label>入座時間</label>
            <span>{{ formatDateTime(reservation.seatedTime) }}</span>
          </div>
          <div v-if="reservation.departureTime" class="detail-item">
            <label>離開時間</label>
            <span>{{ formatDateTime(reservation.departureTime) }}</span>
          </div>
        </div>
      </div>

      <div v-if="reservation.calendarEventId" class="detail-section">
        <h3 class="section-title">Google Calendar</h3>
        <div class="detail-grid">
          <div class="detail-item">
            <label>同步狀態</label>
            <span :class="getSyncStatusBadgeClass(reservation.calendarSyncStatus)">
              {{ getSyncStatusText(reservation.calendarSyncStatus) }}
            </span>
          </div>
          <div v-if="reservation.calendarSyncedAt" class="detail-item">
            <label>最後同步時間</label>
            <span>{{ formatDateTime(reservation.calendarSyncedAt) }}</span>
          </div>
        </div>
      </div>

      <div class="detail-actions">
        <button
          v-if="reservation.status === 'confirmed'"
          class="btn btn-info"
          @click="handleSeat"
        >
          <i class="ri-user-received-line"></i>
          已入座
        </button>
        <button
          v-if="reservation.status === 'confirmed' || reservation.status === 'seated'"
          class="btn btn-success"
          @click="handleCompleteDining"
        >
          <i class="ri-restaurant-line"></i>
          已用餐完畢
        </button>
        <button
          v-if="reservation.status === 'confirmed'"
          class="btn btn-info"
          @click="handleSyncCalendar"
        >
          <i class="ri-calendar-line"></i>
          同步至 Calendar
        </button>
        <button
          v-if="reservation.status !== 'cancelled'"
          class="btn btn-danger"
          @click="handleCancel"
        >
          <i class="ri-close-line"></i>
          取消訂位
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { reservationService } from '@/api/modules/reservations.js';
import { showSuccess, showError, showWarning, showDeleteConfirm, showConfirm } from '@/utils/swal.js';

export default {
  name: 'ReservationDetail',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const reservation = ref(null);
    const loading = ref(false);
    const error = ref(null);
    const selectableTables = ref([]);
    const selectedTableId = ref('');
    const tablesLoading = ref(false);
    const tableLoadError = ref('');
    const savingTable = ref(false);

    const canChangeTable = computed(() => (
      reservation.value
      && ['confirmed', 'seated'].includes(reservation.value.status)
    ));

    const selectedTableOption = computed(() => (
      selectableTables.value.find((table) => table.tableId === selectedTableId.value) || null
    ));

    const canSaveTable = computed(() => (
      Boolean(selectedTableId.value)
      && selectedTableId.value !== (reservation.value?.tableId || '')
      && !tablesLoading.value
    ));

    const normalizeTimeKey = (value) => String(value || '').slice(0, 5);

    const loadAssignableTables = async () => {
      selectableTables.value = [];
      tableLoadError.value = '';
      if (!reservation.value || !canChangeTable.value) return;

      const {
        storeId,
        reservationDate,
        reservationTime,
        guestCount,
        tableId,
        tableNumber,
        tableType,
        isUpwardTableAssignment
      } = reservation.value;

      if (!storeId || !reservationDate || !reservationTime) {
        tableLoadError.value = '缺少門市或訂位時間，無法載入可改派座位';
        return;
      }

      tablesLoading.value = true;
      try {
        const response = await reservationService.getAvailableSlots(
          storeId,
          reservationDate.includes('T') ? reservationDate.split('T')[0] : reservationDate,
          guestCount,
          route.params.id
        );
        const slots = response?.data?.availableSlots || [];
        const matchedSlot = slots.find((slot) => (
          normalizeTimeKey(slot.time) === normalizeTimeKey(reservationTime)
        ));
        const options = [...(matchedSlot?.availableTables || [])];

        // 保留目前座位（即使當下被視為占用／超出範圍，仍可顯示）
        if (tableId && !options.some((item) => item.tableId === tableId)) {
          options.unshift({
            tableId,
            tableNumber: tableNumber || '目前座位',
            tableType: tableType || null,
            minCapacity: null,
            maxCapacity: null,
            matchType: isUpwardTableAssignment ? 'upward' : 'exact',
            isUpwardAssignment: Boolean(isUpwardTableAssignment),
            fitsGuestCount: !isUpwardTableAssignment
          });
        }

        selectableTables.value = options;
        selectedTableId.value = tableId || (options[0]?.tableId || '');
      } catch (err) {
        tableLoadError.value = err.response?.data?.message || err.message || '載入可改派座位失敗';
        selectableTables.value = [];
      } finally {
        tablesLoading.value = false;
      }
    };

    const fetchReservation = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await reservationService.getReservation(route.params.id);
        if (response.success && response.data) {
          reservation.value = response.data;
          selectedTableId.value = response.data.tableId || '';
          await loadAssignableTables();
        } else {
          throw new Error('查詢失敗');
        }
      } catch (err) {
        error.value = err.response?.data?.message || err.message || '查詢訂位詳情失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleSaveTable = async () => {
      if (!canSaveTable.value || !reservation.value) return;

      const selected = selectedTableOption.value;
      if (selected?.isUpwardAssignment) {
        await showWarning(
          '向上相容座位',
          `「${selected.tableNumber}」為向上相容座位，更新後會標示於訂位資料。`
        );
      }

      savingTable.value = true;
      try {
        await reservationService.updateReservation(route.params.id, {
          tableId: selectedTableId.value
        });
        showSuccess('座位已更新');
        const refreshed = await reservationService.getReservation(route.params.id);
        if (refreshed?.success && refreshed.data) {
          reservation.value = refreshed.data;
          selectedTableId.value = refreshed.data.tableId || selectedTableId.value;
        }
        await loadAssignableTables();
      } catch (err) {
        showError(err.response?.data?.message || '更新座位失敗');
      } finally {
        savingTable.value = false;
      }
    };

    const handleEdit = () => {
      router.push(`/client/reservations/${route.params.id}/edit`);
    };

    const handleBack = () => {
      const storeId = reservation.value?.storeId;
      router.push({
        path: '/client/reservations',
        query: storeId ? { storeId } : undefined
      });
    };

    const handleSeat = async () => {
      const confirmed = await showConfirm(
        '設定為已入座？',
        '客人已到店入座。已入座的訂位不會被逾時未到自動取消。',
        '已入座',
        '取消',
        'question'
      );
      if (!confirmed) return;

      try {
        await reservationService.updateReservationStatus(route.params.id, 'seated');
        showSuccess('已入座');
        fetchReservation();
      } catch (err) {
        showError(err.response?.data?.message || '設定已入座失敗');
      }
    };

    const handleCompleteDining = async () => {
      const confirmed = await showConfirm(
        '設定為已用餐完畢？',
        '座位會空出來，並通知下一位人數符合的候位顧客。',
        '已用餐完畢',
        '取消',
        'question'
      );
      if (!confirmed) return;

      try {
        const response = await reservationService.updateReservationStatus(route.params.id, 'completed');
        const notice = response?.data?.waitlistNotification;
        let detail = '目前沒有等待中的候位。';
        if (notice?.notified) {
          const channel = notice.channel === 'line' ? 'LINE' : '簡訊';
          const queue = notice.queueNumber ? `（${notice.queueNumber} 號）` : '';
          detail = `已用${channel}通知候位顧客 ${notice.guestName || ''}${queue}。`;
        } else if (notice?.reason === 'no_match') {
          detail = '目前沒有人數符合這張桌的候位顧客。';
        } else if (notice?.reason === 'disabled') {
          detail = '尚未開啟候位通知。請到訂位參數設定開啟 LINE 或簡訊。';
        } else if (notice?.reason && notice.reason !== 'none') {
          detail = notice.error ? `候位通知未送出：${notice.error}` : '候位通知未送出。';
        }
        showSuccess('已用餐完畢', detail);
        fetchReservation();
      } catch (err) {
        showError(err.response?.data?.message || '設定用餐完畢失敗');
      }
    };

    const handleCancel = async () => {
      const confirmed = await showDeleteConfirm('確定要取消此訂位嗎？');
      if (!confirmed) return;

      try {
        await reservationService.cancelReservation(route.params.id);
        showSuccess('訂位已取消');
        fetchReservation();
      } catch (err) {
        showError(err.response?.data?.message || '取消訂位失敗');
      }
    };

    const handleSyncCalendar = async () => {
      try {
        await reservationService.syncToCalendar(route.params.id);
        showSuccess('已同步至 Google Calendar');
        fetchReservation();
      } catch (err) {
        showError(err.response?.data?.message || '同步失敗');
      }
    };

    const getStatusText = (status) => {
      const statusMap = {
        confirmed: '已確認',
        seated: '已入座',
        completed: '已用餐完畢',
        cancelled: '已取消',
        no_show: '未出席'
      };
      return statusMap[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const classMap = {
        confirmed: 'badge badge-info',
        seated: 'badge badge-warning',
        completed: 'badge badge-success',
        cancelled: 'badge badge-danger',
        no_show: 'badge badge-secondary'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const getSyncStatusText = (status) => {
      const statusMap = {
        pending: '待同步',
        synced: '已同步',
        failed: '同步失敗'
      };
      return statusMap[status] || status;
    };

    const getSyncStatusBadgeClass = (status) => {
      const classMap = {
        pending: 'badge badge-warning',
        synced: 'badge badge-success',
        failed: 'badge badge-danger'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const formatDate = (date) => {
      if (!date) return '-';
      const raw = String(date);
      const dateOnly = raw.includes('T') ? raw.split('T')[0] : raw;
      return new Date(`${dateOnly}T00:00:00`).toLocaleDateString('zh-TW');
    };

    const formatDateTime = (date) => {
      if (!date) return '-';
      return new Date(date).toLocaleString('zh-TW');
    };

    const formatTimeToMinute = (time) => {
      if (!time) return '-';
      return String(time).slice(0, 5);
    };

    const formatTableLabel = (item) => {
      if (!item?.tableId && !item?.tableNumber) return '未分配';
      const number = item.tableNumber || '未命名座位';
      const type = item.tableType ? `（${item.tableType}）` : '';
      return `${number}${type}`;
    };

    const formatSelectableTableLabel = (table) => {
      if (!table) return '';
      const range = (table.minCapacity != null && table.maxCapacity != null)
        ? `${table.minCapacity}-${table.maxCapacity}人`
        : (table.capacity ? `${table.capacity}人` : '');
      const type = table.tableType ? `${table.tableType}` : '座位';
      const upward = table.isUpwardAssignment ? '・向上相容' : '';
      const parts = [table.tableNumber || '未命名', type, range].filter(Boolean);
      return `${parts[0]}（${parts.slice(1).join(' / ')}${upward}）`;
    };

    onMounted(() => {
      fetchReservation();
    });

    return {
      reservation,
      loading,
      error,
      selectableTables,
      selectedTableId,
      selectedTableOption,
      tablesLoading,
      tableLoadError,
      savingTable,
      canChangeTable,
      canSaveTable,
      fetchReservation,
      handleSaveTable,
      handleEdit,
      handleBack,
      handleCancel,
      handleSeat,
      handleCompleteDining,
      handleSyncCalendar,
      getStatusText,
      getStatusBadgeClass,
      getSyncStatusText,
      getSyncStatusBadgeClass,
      formatDate,
      formatDateTime,
      formatTimeToMinute,
      formatTableLabel,
      formatSelectableTableLabel
    };
  }
};
</script>

<style scoped>
.detail-container {
  padding: 20px;
}

.upward-badge {
  margin-left: 0;
  vertical-align: middle;
}

.table-assign-item {
  grid-column: 1 / -1;
}

.table-assign-controls {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
}

.table-select {
  max-width: 360px;
  min-width: 220px;
}

.table-hint {
  color: #64748b;
  flex-basis: 100%;
  font-size: 13px;
  margin: 0;
}

.table-hint-error {
  color: #dc2626;
}

.badge-warning {
  background: #fff7ed;
  border: 1px solid #fdba74;
  border-radius: 999px;
  color: #c2410c;
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 9px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.detail-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.detail-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
}

.detail-item.full-width {
  grid-column: 1 / -1;
}

.detail-item label {
  font-weight: 500;
  color: #666;
  margin-bottom: 4px;
  font-size: 14px;
}

.detail-item span {
  font-size: 16px;
}

.detail-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

@media (max-width: 768px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
